// src/app/components/modal-map/modal-map.ts
import React from 'react'

import { ModalTypes } from './modal-types'

type AnyProps = any
type ModalComponent<P = AnyProps> = React.ComponentType<P>
type ModalLoader<P = AnyProps> = () => Promise<{ default: ModalComponent<P> }>

/**
 * All modal loaders (lazy imports).
 * NOTE: For named exports we wrap: .then(m => ({ default: m.NamedExport }))
 */
export const MODAL_LOADERS: Record<ModalTypes, ModalLoader> = {
  // Preview
  [ModalTypes.COMMENT_PREVIEW]: () => import('@/components/comments/comment-preview-modal'),

  [ModalTypes.EXCEL_PREVIEW]: () => import('@/components/excel-preview').then(m => ({ default: m.ExcelPreviewModal })),

  [ModalTypes.DOCX_PREVIEW]: () => import('@/components/docx-preview').then(m => ({ default: m.DocxPreviewModal })),

  [ModalTypes.PDF_PREVIEW]: () => import('@/components/pdf-preview').then(m => ({ default: m.PdfPreviewModal })),

  [ModalTypes.JSON_PREVIEW]: () => import('@/components/code-preview').then(m => ({ default: m.JsonPreviewModal })),

  [ModalTypes.XML_PREVIEW]: () => import('@/components/code-preview').then(m => ({ default: m.XmlPreviewModal })),

  [ModalTypes.EML_PREVIEW]: () => import('@/components/eml-preview/eml-preview-modal').then(m => ({ default: m.EmlPreviewModal })),

  // Delete confirmations
  [ModalTypes.DELETE_SECURITY_MEASURE]: () =>
    import('@/components/security-measures').then(m => ({ default: m.DeleteSecurityMeasureConfirmationModal })),

  [ModalTypes.DELETE_DEBTOR_STATEMENT]: () =>
    import('@/pages/back-office-pages/risk-management/contract-details/debtor-statements-tab').then(m => ({
      default: m.DeleteDebtorStatementConfirmationModal,
    })),

  [ModalTypes.DELETE_DOCUMENT_CONFIRMATION]: () =>
    import('@/components/risk-reports/delete-document-comfirmation-modal').then(m => ({
      default: m.DeleteDocumentConfirmationModal,
    })),

  // Client (back office)
  [ModalTypes.CLIENT_APPROVAL]: () => import('@/pages/back-office-pages/clients/clients-details/profile-tab/approval/approve-client-modal'),

  [ModalTypes.CLIENT_ACTIVATE_LOAN_BOOK_CONTRACT]: () =>
    import('@/pages/back-office-pages/clients/loan-book-contract-details/overview-tab/actions').then(m => ({
      default: m.ActivateLoanBookContractModal,
    })),

  [ModalTypes.CLIENT_DUPLICATE]: () => import('@/pages/back-office-pages/clients/clients-list/duplicate-cleint-modal'),

  [ModalTypes.CLIENT_CONTRACT_DUPLICATE]: () =>
    import('@/pages/back-office-pages/clients/clients-details/factoring-contracts-tab').then(m => ({
      default: m.DuplicateContractModal,
    })),

  [ModalTypes.FACTORING_CONTRACT_EDIT_SECURITY_MEASURE]: () =>
    import('@/pages/back-office-pages/clients/factoring-contract-details/security-measures-tab').then(m => ({
      default: m.EditFactoringContractSecurityMeasureModal,
    })),

  [ModalTypes.LOAN_BOOK_EDIT_SECURITY_MEASURE]: () =>
    import('@/pages/back-office-pages/clients/loan-book-contract-details/security-measures-tab').then(m => ({
      default: m.EditSecurityMeasureModal,
    })),

  [ModalTypes.CLIENT_UPDATE_SUPPLIER]: () =>
    import('@/pages/back-office-pages/clients/loan-book-contract-details/suppliers-tab').then(m => ({
      default: m.UpdateSupplierModal,
    })),

  // Client -> Debtor relations (back office)
  [ModalTypes.CLIENT_DEBTOR_APPROVE]: () =>
    import('@/pages/back-office-pages/clients/client-debtor-details/relations-tab/actions').then(m => ({
      default: m.ClientDebtorApproveModal,
    })),

  [ModalTypes.CLIENT_DEBTOR_BILLING_ADDRESS]: () =>
    import('@/pages/back-office-pages/clients/client-debtor-details/billing-adderesses-tab/add-billing-address-modal'),

  [ModalTypes.CLIENT_DEBTOR_CLOSE_RELATIONSHIP]: () =>
    import('@/pages/back-office-pages/clients/client-debtor-details/relations-tab/actions').then(m => ({
      default: m.ClientDebtorCloseRelationshipModal,
    })),

  [ModalTypes.CLIENT_DEBTOR_REJECT]: () =>
    import('@/pages/back-office-pages/clients/client-debtor-details/relations-tab/actions').then(m => ({
      default: m.ClientDebtorRejectModal,
    })),

  [ModalTypes.CLIENT_DEBTOR_REQUEST_CLARIFICATION]: () =>
    import('@/pages/back-office-pages/clients/client-debtor-details/relations-tab/actions').then(m => ({
      default: m.ClientDebtorRequestClarificationModal,
    })),

  // Debtor (back office)
  [ModalTypes.DEBTOR_BLOCK_UNBLOCK]: () =>
    import('@/pages/back-office-pages/clients/debtor-details/profile-tab/block-unblock-debtor/debtor-block-unblock-modal'),

  [ModalTypes.DEBTOR_UPDATE_RISK_REPORTS]: () =>
    import('@/pages/back-office-pages/clients/debtor-details/risk-tab/risk-actions/debtor-update-risk-reports-modal'),

  // Invoice task-actions (back office)
  [ModalTypes.INVOICE_ADD_EXTRA_FEES]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.AddExtraFeesForInvoiceModal })),

  [ModalTypes.INVOICE_APPROVE]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.ApproveInvoiceModal })),

  [ModalTypes.INVOICE_ARCHIVE]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.ArchiveInvoiceModal })),

  [ModalTypes.INVOICE_BUY]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.BuyInvoiceModal })),

  [ModalTypes.INVOICE_CLOSE]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.CloseInvoiceModal })),

  [ModalTypes.INVOICE_CLOSE_WITHOUT_CN]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.CloseInvoiceWithoutCNModal })),

  [ModalTypes.INVOICE_BLOCK_CLOSE]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.BlockCloseInvoiceModal })),

  [ModalTypes.INVOICE_UNBLOCK_CLOSE]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.UnblockCloseInvoiceModal })),

  [ModalTypes.INVOICE_FUND]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.FundInvoiceModal })),

  [ModalTypes.INVOICE_MARK_AS_PAID]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.MarkAsPaidInvoiceModal })),

  [ModalTypes.INVOICE_MARK_AS_REPURCHASED]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.MarkAsRepurchasedInvoiceModal })),

  [ModalTypes.INVOICE_PARTLY_FUND]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.PartlyFundInvoiceModal })),

  [ModalTypes.INVOICE_REJECT]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.RejectInvoiceModal })),

  [ModalTypes.INVOICE_RESET_TO_NEW]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.ResetToNewInvoiceModal })),

  [ModalTypes.INVOICE_REVERT_REPURCHASE]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.RevertRepurchaseInvoiceModal })),

  [ModalTypes.INVOICE_REVERT_WRITE_OFF]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.RevertWriteOffInvoiceModal })),

  [ModalTypes.INVOICE_SETTLE]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.SettleInvoiceModal })),

  [ModalTypes.INVOICE_WRITE_OFF]: () => import('@/components/invoice-actions-modals').then(m => ({ default: m.WriteOffInvoiceModal })),

  [ModalTypes.NOTES_UPDATE_PN_FUNDED_DATE]: () =>
    import('@/pages/back-office-pages/factoring/notes-list/update-pn-funded-date-modal').then(m => ({
      default: m.UpdatePnFundedDateModal,
    })),

  // Factoring - AI Tasks
  [ModalTypes.AI_TASK_REDO]: () =>
    import('@/pages/back-office-pages/factoring/ai-task-details/overview-tab/task-actions').then(m => ({
      default: m.RedoAiTaskModal,
    })),

  [ModalTypes.AI_TASK_ARCHIVE]: () =>
    import('@/pages/back-office-pages/factoring/ai-task-details/overview-tab/task-actions').then(m => ({
      default: m.ArchiveAiTaskModal,
    })),

  [ModalTypes.AI_TASK_DOCUMENT_ARCHIVE]: () =>
    import('@/pages/back-office-pages/factoring/ai-task-document-details/overview-tab/document-actions').then(m => ({
      default: m.ArchiveAiTaskDocumentModal,
    })),

  // Risk monitoring task modals
  [ModalTypes.RISK_MONITORING_TASK_REVIEW]: () =>
    import('@/pages/back-office-pages/risk-management/monitoring-task-details/overview-tab/task-actions').then(m => ({
      default: m.ReviewRiskMonitoringTaskModal,
    })),
  [ModalTypes.RISK_MONITORING_TASK_ARCHIVE]: () =>
    import('@/pages/back-office-pages/risk-management/monitoring-task-details/overview-tab/task-actions').then(m => ({
      default: m.ArchiveRiskMonitoringTaskModal,
    })),

  // Bulk verification task modals
  [ModalTypes.INVOICE_BULK_EXTERNAL_VERIFICATION_TASK_ARCHIVE]: () =>
    import('@/pages/back-office-pages/factoring/bulk-external-verification-task-details/overview-tab/task-actions').then(m => ({
      default: m.ArchiveInvoiceBulkVerificationTaskModal,
    })),

  [ModalTypes.INVOICE_BULK_EXTERNAL_VERIFICATION_TASK_RESEND_EMAILS]: () =>
    import(
      '@/pages/back-office-pages/factoring/bulk-external-verification-task-details/overview-tab/task-actions/modals/resend-emails-bulk-external-verification-modal'
    ).then(m => ({ default: m.ResendEmailsForBulkVerificationTaskModal })),

  // Product (loan book payouts)
  [ModalTypes.PRODUCT_APPROVE]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.ApproveProductModal,
    })),

  [ModalTypes.PRODUCT_ARCHIVE]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.ArchiveProductModal,
    })),

  [ModalTypes.PRODUCT_DECLINE]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.DeclineProductModal,
    })),

  [ModalTypes.PRODUCT_CLOSE]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.CloseProductModal,
    })),

  [ModalTypes.PRODUCT_MAKE_PAYOUT]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.MakePayoutModal,
    })),

  [ModalTypes.PRODUCT_ISSUE_FULL_RETURN_REPORT]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.IssueFullReturnModal,
    })),

  [ModalTypes.PRODUCT_REQUEST_APPROVAL]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.RequestProductApprovalModal,
    })),

  [ModalTypes.PRODUCT_REQUEST_CLARIFICATION]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.RequestClarificationForProductModal,
    })),

  [ModalTypes.PRODUCT_RESET_TO_NEW]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/overview-tab/actions/actions-modal').then(m => ({
      default: m.ResetToNewProductModal,
    })),

  [ModalTypes.PRODUCT_EDIT_REVERSE_FACTORING_INVOICE]: () =>
    import('@/pages/back-office-pages/loan-book/payout-details-common/invoices-tab/update-reverse-factopring-invoice-modal'),

  // Payments
  [ModalTypes.PAYMENTS_RE_OPEN_TRANSACTION]: () =>
    import('@/pages/back-office-pages/payments/bank-transaction-details/overview-tab/transaction-actions').then(m => ({
      default: m.ReOpenTransactionModal,
    })),

  [ModalTypes.PAYMENTS_WRITE_OFF_TRANSACTION]: () =>
    import('@/pages/back-office-pages/payments/bank-transaction-details/overview-tab/transaction-actions').then(m => ({
      default: m.WriteOffTransactionModal,
    })),

  [ModalTypes.PAYMENTS_REMOVE_TRANSACTION]: () =>
    import('@/pages/back-office-pages/payments/bank-transaction-details/overview-tab/transaction-actions').then(m => ({
      default: m.RemoveTransactionModal,
    })),

  [ModalTypes.PAYMENTS_UPDATE_GENERAL_LEDGER]: () =>
    import('@/pages/back-office-pages/payments/general-ledgers/ledgers-tab/update-ledger-modal'),

  // System preferences
  [ModalTypes.SYSTEM_UPDATE_FUNDING_LINE]: () =>
    import('@/pages/back-office-pages/system-preferences/system/funding-lines-tab/update-funding-line-modal'),

  [ModalTypes.SYSTEM_EDIT_CLIENT_USER]: () =>
    import('@/pages/back-office-pages/system-preferences/system/client-users-tab/edit-client-user-modal'),

  [ModalTypes.SYSTEM_EDIT_BROKER_USER]: () =>
    import('@/pages/back-office-pages/system-preferences/broker-details/users-tab/edit-broker-user-modal'),

  [ModalTypes.SYSTEM_EDIT_INVESTOR_USER]: () =>
    import('@/pages/back-office-pages/system-preferences/system/investors-tab/edit-investor-user-modal'),

  [ModalTypes.SYSTEM_EDIT_AI_EMAIL]: () => import('@/pages/back-office-pages/system-preferences/system/ai-emails-tab/edit-ai-email-modal'),

  [ModalTypes.SYSTEM_DELETE_AI_EMAIL]: () =>
    import('@/pages/back-office-pages/system-preferences/system/ai-emails-tab/delete-ai-email-confirmation-modal'),

  // Risk management - Annual review contract
  [ModalTypes.ANNUAL_REVIEW_CONTRACT_GENERATE_ADDENDUM]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-contract-details/overview-tab/actions').then(m => ({
      default: m.GenerateAddendumModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_CONTRACT_SYNC_TO_FAB]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-contract-details/overview-tab/actions').then(m => ({
      default: m.SyncToFabModal,
    })),

  // Risk management - Annual review (client)
  [ModalTypes.ANNUAL_REVIEW_APPROVE]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ApproveAnnualReviewClientModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_REJECT]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RejectAnnualReviewClientModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_REQUEST_CLARIFICATION]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RequestClarificationForAnnualReviewClientModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_CONTINUE_APPROVAL]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ContinueApprovalForAnnualReviewClientModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_ARCHIVE]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ArchiveAnnualReviewClientModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_RESET_TO_NEW]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ResetToNewAnnualReviewModal,
    })),

  // Risk management - Annual review (debtor)
  [ModalTypes.ANNUAL_REVIEW_DEBTOR_APPROVE]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ApproveDebtorAnnualReviewDebtorModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_DEBTOR_REJECT]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RejectDebtorAnnualReviewDebtorModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_REQUEST_DEBTOR_CLARIFICATION]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RequestClarificationForDebtorAnnualReviewModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_CONTINUE_DEBTOR_APPROVAL]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ContinueDebtorApprovalForAnnualReviewDebtorModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_DEBTOR_ARCHIVE]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ArchiveDebtorAnnualReviewDebtorModal,
    })),

  [ModalTypes.ANNUAL_REVIEW_DEBTOR_RESET_TO_NEW]: () =>
    import('@/pages/back-office-pages/risk-management/annual-review-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ResetToNewDebtorAnnualReviewDebtorModal,
    })),

  // Prospect / Risk management
  [ModalTypes.PROSPECT_APPROVE]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ApproveProspectModal,
    })),

  [ModalTypes.PROSPECT_REJECT]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RejectProspectModal,
    })),

  [ModalTypes.PROSPECT_CLOSE]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.CloseProspectModal,
    })),

  [ModalTypes.PROSPECT_REQUEST_APPROVAL]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RequestProspectApprovalModal,
    })),

  [ModalTypes.PROSPECT_ARCHIVE]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ArchiveProspectModal,
    })),

  [ModalTypes.PROSPECT_RESET_TO_NEW]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ResetToNewProspectModal,
    })),

  [ModalTypes.PROSPECT_REQUEST_CLARIFICATION]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RequestClarificationForProspectModal,
    })),

  [ModalTypes.CREATE_CLIENT]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.CreateClientModal,
    })),

  [ModalTypes.LEGACY_CREATE_CLIENT]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.LegacyCreateClientModal,
    })),

  // Prospect debtor
  [ModalTypes.PROSPECT_DEBTOR_APPROVE]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.ApproveProspectDebtorModal,
    })),

  [ModalTypes.PROSPECT_DEBTOR_REJECT]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RejectProspectDebtorModal,
    })),

  [ModalTypes.PROSPECT_DEBTOR_CLOSE]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.CloseProspectDebtorModal,
    })),

  [ModalTypes.PROSPECT_DEBTOR_REQUEST_APPROVAL]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RequestProspectDebtorApprovalModal,
    })),

  [ModalTypes.PROSPECT_DEBTOR_REQUEST_CLARIFICATION]: () =>
    import('@/pages/back-office-pages/risk-management/prospect-debtor-details/profile-tab/actions/actions-modal').then(m => ({
      default: m.RequestClarificationForProspectDebtorModal,
    })),

  // Prospect contract
  [ModalTypes.PROSPECT_GENERATE_CONTRACT]: () =>
    import('@/pages/back-office-pages/risk-management/contract-details/profile-tab/generate-contract').then(m => ({
      default: m.GenerateContractModal,
    })),

  [ModalTypes.PROSPECT_CONTRACT_EDIT_SECURITY_MEASURE]: () =>
    import('@/pages/back-office-pages/risk-management/contract-details/security-measures-tab/edit-security-measure-modal'),

  [ModalTypes.PROSPECT_CONTRACT_EDIT_DEBTOR_STATEMENT]: () =>
    import('@/pages/back-office-pages/risk-management/contract-details/debtor-statements-tab').then(m => ({
      default: m.EditProspectDebtorStatementModal,
    })),

  // Loan book
  [ModalTypes.RESCHEDULE_LOAN_BOOK_TASK]: () =>
    import('@/pages/back-office-pages/loan-book/debt-collection-details/open-tasks-tab').then(m => ({
      default: m.RescheduleLoanBookTaskModal,
    })),

  // Investment / Investor
  [ModalTypes.INVESTOR_APPROVE]: () =>
    import('@/pages/back-office-pages/investment/investor-details/profile-tab/actions/action-modals').then(m => ({
      default: m.ApproveInvestorModal,
    })),

  [ModalTypes.INVESTOR_ACTIVATE]: () =>
    import('@/pages/back-office-pages/investment/investor-details/profile-tab/actions/action-modals').then(m => ({
      default: m.ActivateInvestorModal,
    })),

  [ModalTypes.INVESTOR_ARCHIVE]: () =>
    import('@/pages/back-office-pages/investment/investor-details/profile-tab/actions/action-modals').then(m => ({
      default: m.ArchiveInvestorModal,
    })),

  [ModalTypes.INVESTOR_RESET_TO_NEW]: () =>
    import('@/pages/back-office-pages/investment/investor-details/profile-tab/actions/action-modals').then(m => ({
      default: m.ResetToNewInvestorModal,
    })),

  [ModalTypes.INVESTOR_REQUEST_APPROVAL]: () =>
    import('@/pages/back-office-pages/investment/investor-details/profile-tab/actions/action-modals').then(m => ({
      default: m.RequestInvestorApprovalModal,
    })),

  [ModalTypes.INVESTOR_EDIT_LEGAL_REPRESENTATIVE]: () =>
    import('@/pages/back-office-pages/investment/investor-details/kyc-tab/legal-representatives-card').then(m => ({
      default: m.EditInvestorLegalRepresentativeModal,
    })),

  [ModalTypes.INVESTOR_EDIT_UBO]: () =>
    import('@/pages/back-office-pages/investment/investor-details/kyc-tab/ubos-card').then(m => ({
      default: m.EditInvestorUBOModal,
    })),

  [ModalTypes.INVESTOR_CANCEL_REINVESTMENT_PLAN]: () =>
    import('@/pages/back-office-pages/investment/reinvestment-details/overview-tab/actions/action-modals').then(m => ({
      default: m.CancelReinvestmentPlanModal,
    })),

  [ModalTypes.INVESTOR_REINVESTMENT_PLAN_SEND_NOTIFICATION]: () =>
    import('@/pages/back-office-pages/investment/task-details/overview-tab/reinvestments-card').then(m => ({
      default: m.SendNotificationToInvestorModal,
    })),

  [ModalTypes.INVESTOR_REQUEST_INVEST_IN_NEW_OFFER]: () =>
    import('@/pages/back-office-pages/investment/task-details/overview-tab/actions/invest-in-new-offer-modal').then(m => ({
      default: m.RequestInvestInNewOfferModal,
    })),

  [ModalTypes.INVESTOR_CREATE_NEW_INVESTOR]: () =>
    import('@/pages/back-office-pages/investment/task-details/overview-tab/actions/new-investor-modal').then(m => ({
      default: m.CreateNewInvestorModal,
    })),

  // Client portal (invoice task-actions are from invoice-task-actions-modals)
  [ModalTypes.CLIENT_PORTAL_ARCHIVE_INVOICE]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.ArchiveInvoiceClientPortalModal })),

  [ModalTypes.CLIENT_PORTAL_BILLING_ADDRESS]: () =>
    import('@/pages/client-pages/debtor-details/billing-adderesses-tab/add-billing-address-modal'),

  [ModalTypes.CLIENT_PORTAL_EDIT_USER]: () => import('@/pages/client-pages/users-list/edit-user-modal'),

  [ModalTypes.CLIENT_PORTAL_FUND_INVOICE]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.FundInvoiceClientPortalModal })),

  [ModalTypes.CLIENT_PORTAL_PARTLY_FUND_INVOICE]: () =>
    import('@/components/invoice-actions-modals').then(m => ({ default: m.PartlyFundInvoiceClientPortalModal })),

  [ModalTypes.CLIENT_PORTAL_REQUEST_CLOSE_CLIENT_DEBTOR_RELATIONSHIP]: () =>
    import('@/pages/client-pages/debtor-details/profile-tab/actions/client-portal-request-close-client-debtor-relationship-modal'),
}

// ---- helpers (cache + optional preload) ----

const LAZY_CACHE: Partial<Record<ModalTypes, React.LazyExoticComponent<ModalComponent>>> = {}

export function getLazyModal(type: ModalTypes): React.LazyExoticComponent<ModalComponent> {
  const cached = LAZY_CACHE[type]
  if (cached) return cached

  const loader = MODAL_LOADERS[type]
  if (!loader) throw new Error(`No modal loader registered for modal type: ${String(type)}`)

  const Lazy = React.lazy(loader)
  LAZY_CACHE[type] = Lazy
  return Lazy
}

export function preloadModal(type: ModalTypes): void {
  const loader = MODAL_LOADERS[type]
  if (loader) void loader()
}
